import React, { createContext, useState, useEffect, useContext } from 'react';
import { notificationService } from '../services/notificationService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export function useNotifications() {
    return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
    const { user } = useAuth();
    const [notificationPermission, setNotificationPermission] = useState('default');
    const [fcmToken, setFcmToken] = useState(null);
    const [isSupported, setIsSupported] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    // Add a separate state to track if notifications are disabled by user action
    const [isUserDisabled, setIsUserDisabled] = useState(false);

    useEffect(() => {
        initializeNotifications();
    }, []);

    useEffect(() => {
        if (user && fcmToken && !isUserDisabled) {
            notificationService.saveTokenToDatabase(user.uid, fcmToken, user.groupId);
        }
    }, [user, fcmToken, isUserDisabled]);

    const initializeNotifications = async () => {

        try {
            // Wait for the service to be fully initialized
            await notificationService.ensureInitialized();

            // Now safely read the values
            setIsSupported(notificationService.isSupported);
            setNotificationPermission(notificationService.getPermissionStatus());
            setIsInitialized(true);

            console.log('Notification service initialized:', {
                isSupported: notificationService.isSupported,
                permission: notificationService.getPermissionStatus()
            });
        } catch (error) {
            console.error('Failed to initialize notifications:', error);
            setIsSupported(false);
            setIsInitialized(true);
        }
    };

    const requestNotificationPermission = async () => {
        try {
            const token = await notificationService.requestPermission();
            setFcmToken(token);
            setNotificationPermission('granted');
            setIsUserDisabled(false); // Re-enable when user grants permission
            console.log('[requestNotificationPermission]----->',);
            if (user && token) {
                await notificationService.saveTokenToDatabase(user.uid, token, user.groupId);
            }

            return token;
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            setNotificationPermission('denied');
            throw error;
        }
    };

    const disableNotifications = async () => {
        try {
            // Remove token from database if exists
            if (user && fcmToken) {
                await notificationService.removeTokenFromDatabase(user.uid, fcmToken);
            }

            // Clear the FCM token from Firebase
            await notificationService.clearToken();

            // Clear the local token state
            setFcmToken(null);

            // Mark as user-disabled (this is key for app-level state)
            setIsUserDisabled(true);

            console.log('Notifications disabled successfully');
        } catch (error) {
            console.error('Error disabling notifications:', error);
        }
    };

    // The effective permission status considering both browser permission and user preference
    const getEffectivePermissionStatus = () => {
        if (isUserDisabled) return 'disabled';
        return notificationPermission;
    };

    const value = {
        notificationPermission: getEffectivePermissionStatus(),
        fcmToken: isUserDisabled ? null : fcmToken,
        isSupported,
        isInitialized,
        isPermissionGranted: notificationPermission === 'granted' && !isUserDisabled,
        requestNotificationPermission,
        disableNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}